import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import { Alert, Button, Divider, Input, Modal, Steps } from "antd";
import { toast } from "react-toastify";
import { getUserRoleFromToken } from "../../utils/Auth";
import {
	codeFormatter,
	currencySymbol,
	priceFormatterByCurrency,
	valueDateFormatter,
} from "../../utils/formatters";
import {
	dualMoneyColumns,
	formatDualMoneyAmount,
} from "../../utils/moneyColumns";
import { CURRENCY_EUR } from "../../api/currency";
import useCurrencies from "../../hooks/useCurrencies";
import {
	CheckOutlined,
	CloseOutlined,
	ExclamationCircleFilled,
} from "@ant-design/icons";
import api from "../../utils/axios";
import "./index.css";
import { initialTableData } from "../../lib/InitialOrderStatus";
import FilterSettlementsSection from "../../components/FilterSettlementsSection";
import PaginationTable from "../../components/Pagination";
import {
	exportDataToCsv,
	formattedExportParams,
	moneyExportColumns,
} from "../../utils/exportDataToCsv";
import classes from "../../components/FilterOrderSection.module.css";
import DefaultInput from "../../components/form/DefaultInput";
import DefaultSelect from "../../components/form/DefaultSelect";
import { ReactComponent as CodeIcon } from "../../assets/codeinput.svg";
import OrderDetailRow from "../../components/form/OrderDetailRow";
import { ReactComponent as QrCodeIcon } from "../../assets/qr-code.svg";
import { fetchSettlementDisableRequestValidation } from "../../lib/SettlementValidationApi";
import {
	documentTypes,
	settlementStatuses,
	userRoles,
} from "../../types/enums";
import { useNavigate } from "react-router";
import useLang from "../../hooks/useLang";
import { navigateToClientDetails } from "../../utils/navigateToClientDetails";
import { useMediaQuery } from "@mui/material";
import { hasAccess } from "../../lib/MenuValidation";

interface ISettlementData {
	id: number;
	applicantFullName: string;
	applicantId: number;
	confirmedDate: string;
	createdAt: string;
	numberOfOrders: number;
	settlementDescription: string;
	settlementStatusDescription: string;
	settlementStatusId: number;
	sumValue: number;
	/**
	 * The EUR half of the same settlement. Independent of `sumValue` — the two are paid out as
	 * separate cash-register transactions and must never be added together. Zero outside PostmanAL.
	 */
	sumValueEur?: number;
}
const { TextArea } = Input;
const hexToRgba = (hex: string, alpha: number) => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r},${g},${b},${alpha})`;
};

const SettlementRefusal = () => {
	// Right-click → Export to Excel / CSV. Without this AG-Grid 27 exports the value *before*
	// the valueFormatter, which is where every currency symbol on this page is added.
	let defaultExcelExportSettings = formattedExportParams;
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [settlementOrderTableColumns, setSettlementOrderTableColumns] =
		useState<ColDef[]>();
	const [tableData, setTableData] = useState<any>([]);
	const [open, setOpen] = useState<boolean>(false);
	const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);
	const [settlementDescription, setSettlementDescription] =
		useState<string>("");
	// Known from the token on the first render. Starting from guesses (return-warehouse manager:
	// true) and correcting them in an effect made the filter bar load its lists for the wrong role
	// first.
	const [isAdmin, setIsAdmin] = useState<boolean>(
		() => getUserRoleFromToken() === userRoles.ADMIN
	);
	const [isClient, setIsClient] = useState<boolean>(
		() => getUserRoleFromToken() === userRoles.CLIENT
	);
	const [isReturnWarehouseManager, setIsReturnWarehouseManager] =
		useState<boolean>(
			() => getUserRoleFromToken() === userRoles.RETURN_WAREHOUSEMANAGER
		);
	const [refusedOrders, setRefusedOrders] = useState<any>();
	const [staffNameById, setStaffNameById] = useState<Record<string, string>>(
		{}
	);
	const [settlementId, setSettlementId] = useState<number>();
	const [settlementRefusal, setSettlementRefusal] = useState<any>();
	const [orderTableColumns, setOrderTableColumns] = useState<ColDef[]>();
	const [ordersCount, setOrdersCount] = useState<any[]>(initialTableData);
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [openSettlementOrderModal, setOpenSettlementOrderModal] =
		useState<boolean>(false);
	const [openClientDeliverModal, setOpenClientDeliverModal] =
		useState<boolean>(false);
	const [
		openStatusChangeModalConfirmation,
		setOpenStatusChangeModalConfirmation,
	] = useState<boolean>(false);
	const [settlementOrderData, setSettlementOrderData] = useState<any>();
	const [settlementRefusalData, setSettlementRefusalData] = useState<any>();
	const [filteredSettlements, setFilteredSettlements] = useState<any>();
	const [filteredRefusedOrders, setFiteredRefusedOrders] = useState<any>([]);
	const [
		isCreateRefusedSettlementModalOpen,
		setIsCreateRefusedSettlementModalOpen,
	] = useState<boolean>(false);
	const [settlementData, setSettlementData] = useState<ISettlementData>();
	const [settlementHistory, setSettlementHistory] = useState<any[]>([]);
	const [openSettlementHistory, setOpenSettlementHistory] =
		useState<boolean>();
	const [
		openModalSmallDetailsAndHistory,
		setOpenModalSmallDetailsAndHistory,
	] = useState<boolean>(false);
	const [disableClientRequest, setDisableClientRequest] =
		useState<boolean>(false);
	const [openRequestModal, setOpenRequestModal] = useState<boolean>(false);

	const [openScanModal, setOpenScanModal] = useState<boolean>(false);
	const [scannedOrderId, setScannedOrderId] = useState<any>("");
	const [scannedOrdeDetails, setScannedOrdeDetails] = useState<any[]>([]);

	const [settlementModalDescription, setSettlementModalDescription] =
		useState<string>("");
	const { strings } = useLang("settlementRefusal");
	const { dualCurrencyEnabled, defaultCurrencyId } = useCurrencies();
	const canProcessRefusals = hasAccess("PROCESS_SETTLEMENT_REFUSAL_ACCESS");

	const [staffOptions, setStaffOptions] = useState<any>([]);
	// Raw list, also handed to the filter bar so it does not request it a second time.
	const [staffList, setStaffList] = useState<any[] | null>(null);
	const [selectedStaff, setSelectedStaff] = useState(null);
	const navigate = useNavigate();

	const gridRef = useRef<AgGridReact | null>(null);
	let refusalGridRef = useRef(null);
	let scanedOrderGridRef = useRef(null);
	let settlementOrderGridRef = useRef(null);
	let settlementOrderRef = useRef(null);

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const onPageChanged = (page: number, pageSize: number) => {
		setCurrentPage(page);
		getSettlements(
			pageSize,
			page,
			filteredSettlements?.statusIds,
			filteredSettlements?.code,
			filteredSettlements?.orderDescription,
			filteredSettlements?.fromDate,
			filteredSettlements?.toDate,
			filteredSettlements?.sellerIds,
			filteredSettlements?.staffIds
		);
	};
	const handlePageSizeChange = async (current: number, size: number) => {
		setPageSize(size);
	};

	const startOrderIndex = (currentPage - 1) * pageSize + 1;
	const endOrderIndex = Math.min(startOrderIndex + pageSize - 1, total);

	const [currentPage2, setCurrentPage2] = useState(1);
	const [pageSize2, setPageSize2] = useState(20);
	const onPageChanged2 = (page: any) => {
		setCurrentPage2(page);
	};
	const handlePageSizeChange2 = (current: number, size: number) => {
		setPageSize2(size);
		setCurrentPage2(1);
	};

	const paginatedOrderRefusals = useMemo(() => {
		const dataToPaginate = filteredRefusedOrders;
		const startIndex = (currentPage2 - 1) * pageSize2;
		const endIndex = startIndex + pageSize2;
		return dataToPaginate?.slice(startIndex, endIndex);
	}, [currentPage2, filteredRefusedOrders, pageSize2]);

	const totalOrders2 = refusedOrders?.length || 0;
	const startOrderIndex2 = (currentPage2 - 1) * pageSize2 + 1;
	const endOrderIndex2 = Math.min(
		startOrderIndex2 + pageSize2 - 1,
		totalOrders2
	);

	const getStaffData = async () => {
		try {
			const response = await api.get("User/Staff");
			if (response.data) {
				setStaffList(response.data);
				const nameById: Record<string, string> = {};
				response.data.forEach((staffItem: any) => {
					const firstName = staffItem.firstName ?? "";
					const lastName = staffItem.lastName ?? "";
					nameById[staffItem.id] = `${firstName} ${lastName}`.trim();
				});
				setStaffNameById(nameById);

				const results = response.data
					.filter(
						(item: any) =>
							item.userRoleId ===
							userRoles.RETURN_WAREHOUSEMANAGER
					)
					.map((staffItem: any, index: number) => ({
						value: `${staffItem.id}-${index}`,
						label: `${staffItem.firstName} ${staffItem.lastName} (${staffItem.userRole})`,
					}));
				setStaffOptions(results);
			}
		} catch (error: any) {
			console.log(error);
		}
	};

	const getOrderDetails = useCallback(async (userId: number | null) => {
		try {
			const response = await api.get("Order/fetch-refused-orders", {
				params: {
					userId: userId ?? null,
				},
			});
			if (response.status === 200 && response?.data) {
				const data = response?.data;
				// const rejectedOrders = data?.filter(
				// 	(item: any) =>
				// 		item.statusId === orderStatuses.REJECTED ||
				// 		item.statusId === orderStatuses.RETURN_STORAGE
				// );

				setRefusedOrders(data);
				setFiteredRefusedOrders(data);
			}
		} catch (error) {
			console.error("Error fetching order:", error);
		}
	}, []);

	const fetchSettlementHistory = async (settlementId: string) => {
		try {
			const response = await api.get(
				"Settlement/short-settlement-history",
				{
					params: {
						settlementId: settlementId,
					},
				}
			);
			if (response.status === 200) {
				const result = response?.data?.reverse();
				setSettlementHistory(result);
			}
		} catch (error) {
			console.log("Error:", error);
		}
	};

	const fetchTableData = async () => {
		getSettlements(
			pageSize,
			currentPage,
			filteredSettlements?.statusIds,
			filteredSettlements?.code,
			filteredSettlements?.orderDescription,
			filteredSettlements?.fromDate,
			filteredSettlements?.toDate,
			filteredSettlements?.sellerIds,
			filteredSettlements?.staffIds
		);
	};

	const fetchSettlements = async (
		pageSize: number,
		currentPage: number,
		statusIds?: any,
		code?: any,
		orderDescription?: any,
		fromDate?: any,
		toDate?: any,
		sellerIds?: any,
		staffIds?: any,
		paginationDisabled?: boolean
	) => {
		const displayIdSearchTerm = code || null;
		const seller = sellerIds?.split("-")[0];
		const response = await api.post(
			"SettlementRefusal/select-settlement-refusals-paginated",
			{
				pageSize,
				currentPage,
				statusIds: statusIds ?? null,
				code: displayIdSearchTerm ?? null,
				orderDescription: orderDescription ?? null,
				fromDate: fromDate ?? null,
				toDate: toDate ?? null,
				sellerIds: parseInt(seller) ?? null,
				staffIds: parseInt(staffIds) ?? null,
				paginationDisabled,
			}
		);
		return response.data;
	};

	// Null until loaded, which tells the filter bar to wait for it rather than fetch it itself.
	const [seller, setSeller] = useState<any[] | null>(null);

	const getSellerData = useCallback(async () => {
		try {
			const response = await api.get("User/select-small-client-details");
			if (response.data) {
				setSeller(response?.data);
			}
		} catch (error: any) {
			console.log(error);
		}
	}, []);

	const fetchSettlementValidation = async () => {
		try {
			const response = await api.get(
				"Settlement/settlement-validation-for-description",
				{ params: { documentTypeId: 10 } }
			);
			if (response.status === 200) {
				const result = response?.data;
				if (result) {
					const currentTime = new Date();
					const statusDate = new Date(result.statusDate);
					const timeDifference = Date.now() - statusDate.getTime();
					const hoursDifference = timeDifference / (1000 * 60 * 60);
					if (
						(result.currentSettlements === 0 &&
							result.deliveredOrders === 0) ||
						(result.currentSettlements > 0 &&
							result.deliveredOrders === 0)
					) {
						setSettlementModalDescription(
							"Nuk keni porosi te refuzuara ne depon per kthim"
						);
						// not allowed
					} else if (
						result.currentSettlementStatus ===
							settlementStatuses.DELIVERED &&
						result.currentSettlementStatus !== 0
					) {
						setSettlementModalDescription(
							"Nuk mund te kerkoni refuzim te ri pa e konfirmuar refuzim e kaluar"
						);
					} else if (
						result.currentSettlementStatus ===
						settlementStatuses.TRANSPORTING
					) {
						setSettlementModalDescription(
							"Nuk mund te kerkoni refuzim te ri. Per momentin e keni nje refuzim ne transport. "
						);
					} else if (
						result.currentSettlementStatus ===
						settlementStatuses.PENDING
					) {
						setSettlementModalDescription(
							"Nuk mund te kerkoni refuzim te ri. Per momentin e keni nje refuzim ne pritje."
						);
					} else if (
						result.currentSettlementStatus ===
						settlementStatuses.REQUESTED
					) {
						setSettlementModalDescription(
							"Nuk mund te kerkoni refuzim te ri. Per momentin e keni nje refuzim te regjistruar. "
						);
					}
				} else {
					setSettlementModalDescription(
						"Nuk mund te kerkoni refuzim."
					);
				}
			}
		} catch (error) {
			console.log(error);
		}
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	const generateTableColumns = useCallback(
		(isSmallScreen: any, userRoleId: any) => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 40,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "id",
				headerName: strings?.code,
				valueFormatter: (params: any) => codeFormatter(params.value),
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "settlementStatusDescription",
				headerName: strings?.status,
				cellRendererFramework: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#ffff";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								<p className="orderStatusDescription">
									{params.value}
								</p>
							</div>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "applicantUserName",
				headerName: strings?.applicantUsername,
				cellRendererFramework: (params: any) => {
					const sellerUserName = params.data.applicantUserName;

					const sellerUniqueId = params.data.uniqueSellerIdentifier;
					const sellerString: string =
						sellerUserName + "(" + sellerUniqueId + ")";
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{sellerString}
							</p>
						</div>
					);
				},
			},
			{
				field: "applicantFullName",
				headerName: strings?.applicant,
				cellStyle: () => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					fontFamily: "Inter",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: "Emri Biznesit",
				field: "businessName",
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "createdAt",
				headerName: strings?.date,
				valueFormatter: (params: any) =>
					valueDateFormatter(params?.value),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "numberOfOrders",
				headerName: strings?.orderCount,
				suppressSizeToFit: isSmallScreen,
			},
			// A refusal batch carries both `sumValue` and `sumValueEur`; each gets its own column
			// on PostmanAL, and everywhere else this collapses back to the single "Shuma".
			...dualMoneyColumns({
				field: "sumValue",
				headerName: strings?.value,
				dualCurrencyEnabled,
				defaultCurrencyId,
				width: 150,
				suppressSizeToFit: isSmallScreen,
			}),
			{
				field: "settlementDescription",
				headerName: strings?.comments,
				suppressSizeToFit: isSmallScreen,
			},
			// {
			// 	field: "pergjegjesi",
			// 	headerName: "Pergjegjesi",
			// },
			{
				field: "confirmedDate",
				headerName: strings?.confirmed,
				cellRenderer: (params: any) => {
					if (
						(params?.data.settlementStatusId ===
							settlementStatuses.RETURN_STORAGE ||
							params?.data.settlementStatusId ===
								settlementStatuses.DELIVERED) &&
						userRoleId === userRoles.CLIENT
					) {
						return (
							<Button
								// icon={<CheckCircleOutlined />}
								style={{ color: "green" }}
							>
								{strings?.confirm}
							</Button>
						);
					} else if (
						params?.data.settlementStatusId ===
						settlementStatuses.CONFIRMED
					) {
						return valueDateFormatter(params?.value);
					} else {
						return "-";
					}
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "open",
				headerName: " ",
				cellRenderer: (params: any) => {
					if (
						(isAdmin ||
							userRoleId === userRoles.RETURN_WAREHOUSEMANAGER) &&
						params?.data.settlementStatusId ===
							settlementStatuses.REQUESTED &&
						canProcessRefusals
					) {
						return (
							<Button
								size="large"
								style={{ color: "green" }}
								// icon={<CheckCircleOutlined />}
							>
								{strings?.proccesRefusals}
							</Button>
						);
					} else if (
						params?.data.settlementStatusId !==
						settlementStatuses.REQUESTED
					) {
						return (
							<>
								<Button
									// icon={<CheckCircleOutlined />}
									style={{ color: "green" }}
								>
									{strings?.open}
								</Button>
							</>
						);
					} else {
						return "-";
					}
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "deliveredBy",
				headerName: strings?.driver,
				hide: true,
			},
			{
				field: "transportingDate",
				headerName: strings?.transportingDate,
				hide: true,
			},
			{
				field: "processedBy",
				headerName: strings?.procesedBy,
				hide: true,
			},
			{
				field: "gatheringCity",
				headerName: "Rajoni i mbledhjes",
				hide: true,
			},
			{
				headerName: "",
				cellRenderer: () => (
					<QrCodeIcon onClick={settlementDetailsPdf} />
				),
				suppressSizeToFit: isSmallScreen,
			},
		],
		[
			canProcessRefusals,
			isAdmin,
			// The currency flags resolve after first paint; without them here the grid keeps the
			// single-column layout it was first built with.
			dualCurrencyEnabled,
			defaultCurrencyId,
			strings?.applicant,
			strings?.applicantUsername,
			strings?.code,
			strings?.comments,
			strings?.confirm,
			strings?.confirmed,
			strings?.date,
			strings?.driver,
			strings?.open,
			strings?.orderCount,
			strings?.proccesRefusals,
			strings?.procesedBy,
			strings?.status,
			strings?.value,
		]
	);

	const generateOrderTableColumns = useCallback(
		(isSmallScreen: any) => [
			{
				headerName: "Select-All",
				width: 80,
				headerCheckboxSelection: true,
				// checkboxSelection: true,
				checkboxSelection: (params: any) => {
					return !!params.data && params.data.active === false;
				},
				// showDisabledCheckboxes: true,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "displayId",
				headerName: strings?.code,
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					fontFamily: "Inter",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "refid",
				headerName: "ReId",
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "lastHistoryUserId",
				headerName: strings?.responsible,
				valueGetter: (params: any) =>
					staffNameById[params.data?.lastHistoryUserId] ?? "",
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "statusDescription",
				headerName: strings?.status,
				cellRendererFramework: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#ffff";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								<p className="orderStatusDescription">
									{params.value}
								</p>
							</div>
						</div>
					);
				},
				width: 170,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.sellerUsername,
				cellRendererFramework: (params: any) => {
					const sellerUserName = params.data.sellerUserName;

					const sellerUniqueId = params.data.uniqueSellerIdentifier;
					const sellerString: string =
						sellerUserName + "(" + sellerUniqueId + ")";
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{sellerString}
							</p>
						</div>
					);
				},
			},
			{
				field: "sellerNameAndSurname",
				headerName: strings?.seller,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "statusChangeComment",
				headerName: strings?.comment,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "createdAt",
				headerName: strings?.createdAt,
				valueFormatter: (params: any) =>
					valueDateFormatter(params?.value),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "refusedDate",
				headerName: strings?.refusedDate,
				valueFormatter: (params: any) =>
					valueDateFormatter(params?.value),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderDescription",
				headerName: strings?.orderDescription,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyerNameAndSurname",
				headerName: strings?.buyer,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "city",
				headerName: strings?.city,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "active",
				headerName: strings?.selected,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyerPhoneNo",
				headerName: strings?.buyerPhoneNo,
				suppressSizeToFit: isSmallScreen,
			},
		],
		[
			staffNameById,
			strings?.buyer,
			strings?.buyerPhoneNo,
			strings?.city,
			strings?.code,
			strings?.comment,
			strings?.createdAt,
			strings?.orderDescription,
			strings?.refusedDate,
			strings?.responsible,
			strings?.selected,
			strings?.seller,
			strings?.sellerUsername,
			strings?.status,
		]
	);

	const generateSettlementTableColumns = useCallback(
		(isSmallScreen: any) => [
			{
				field: "displayId",
				headerName: strings?.code,
				valueFormatter: (params: any) => codeFormatter(params.value),
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
				}),
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "refId",
				headerName: "RefId",
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderPrice",
				headerName: strings?.orderPrice,
				// Each order carries the currency it was registered in.
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "transportPrice",
				headerName: strings?.transportPrice,
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderDescription",
				headerName: strings?.orderDescription,
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "settlementStatusDescription",
				headerName: strings?.settlementStatus,
				width: 100,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyer",
				headerName: strings?.buyer,
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
		],
		[
			strings?.buyer,
			strings?.code,
			strings?.orderDescription,
			strings?.orderPrice,
			strings?.settlementStatus,
			strings?.transportPrice,
		]
	);

	const handleUserRole = useCallback(() => {
		const userRoleId = getUserRoleFromToken();
		setIsAdmin(userRoleId === userRoles.ADMIN);
		setIsClient(userRoleId === userRoles.CLIENT);
		setIsReturnWarehouseManager(
			userRoleId === userRoles.RETURN_WAREHOUSEMANAGER
		);
		return userRoleId;
	}, []);

	useEffect(() => {
		const userRole = handleUserRole();

		setTableColumns(generateTableColumns(isSmallScreen, userRole));
		setOrderTableColumns(generateOrderTableColumns(isSmallScreen));
		setSettlementOrderTableColumns(
			generateSettlementTableColumns(isSmallScreen)
		);
		//fetchSettlements();
	}, [
		generateOrderTableColumns,
		generateSettlementTableColumns,
		generateTableColumns,
	]);

	useEffect(() => {
		fetchSettlementValidation();

		getOrderDetails(null);
		fetchTableData();
		getSellerData();
		getStaffData();
	}, []);

	const getSettlementOrders = async (settlementId: string) => {
		try {
			const response = await api.get(
				"SettlementRefusal/select-refused-settlement-orders",
				{ params: { settlementId: settlementId } }
			);
			if (response.status === 200) {
				setSettlementOrderData(response?.data);
			}
		} catch (error) {
			console.log("Error:" + error);
		}
	};
	const [
		setSettlementRefusalRequestDisabled,
		isSetSettlementRefusalRequestDisabled,
	] = useState(false);

	const onSettlementRefusalRequestSend = async () => {
		isSetSettlementRefusalRequestDisabled(true);
		const params = {
			settlementDescription: settlementDescription,
		};
		try {
			const response = await api.post(
				"SettlementRefusal/request-settlement-refusal",
				params
			);
			if (response.status === 200) {
				toast.success(
					"Kerkesa per barazim te refuzimeve u regjistrua me sukses!"
				);
				setSettlementDescription("");
				setOpenRequestModal(false);
				await fetchTableData();
			}
		} catch (error: any) {
			toast.error(
				"Një gabim ndodhi gjatë krijimit. Ju lutemi provoni përsëri më vonë"
			);
		} finally {
			isSetSettlementRefusalRequestDisabled(false);
		}
	};

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "open":
				await openRefusalModal(params?.data);
				break;
			case "confirmedDate":
				await settlementRefusalConfirm(params?.data);
				break;
			case "id":
				await onSettlementClicked(params?.data);
				break;
			case "applicantFullName":
				await navigateToClientDetails(params?.data?.applicantId);
				break;
		}
	};

	const onRefusedOrdersCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "displayId":
				await onOrderIdClicked(params?.data);
				break;
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.id?.toString();
		// navigate("/order-details", { state: orderId });
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const onSettlementClicked = async (data: any) => {
		fetchSettlementHistory(data?.id);
		setSettlementData(data);
		setOpenModalSmallDetailsAndHistory(true);
	};
	const openRefusalModal = async (data: any) => {
		if (data && data?.settlementStatusId >= settlementStatuses.PROCESSING) {
			await getSettlementOrders(data?.id);
			setSettlementRefusalData(data);
			setSettlementId(data?.id);
			setOpenSettlementOrderModal(true);
		}
		if (
			(isAdmin || isReturnWarehouseManager) &&
			data?.settlementStatusId === settlementStatuses.REQUESTED &&
			canProcessRefusals
		) {
			const userId = data?.applicantId;
			const settlemntId = data?.id;
			setSettlementId(settlemntId);
			await getOrderDetails(userId);
			setOpenDetailsModal(true);
			setSettlementRefusalData(data);
		}
	};

	const settlementRefusalConfirm = (data: any) => {
		if (
			data &&
			(data?.settlementStatusId === settlementStatuses.DELIVERED ||
				data?.settlementStatusId === settlementStatuses.RETURN_STORAGE)
		) {
			setOpen(true);
			setSettlementId(data?.id);
			setSettlementRefusalData(data);
		}
	};

	const [settlementRefusalDisabled, isSettlementRefusalsDisabled] =
		useState(false);

	const settlementRefusalProcessing = async () => {
		isSettlementRefusalsDisabled(true);
		const orderInsert = (refusalGridRef?.current as any).api
			.getSelectedRows()
			.map((item: any) => ({
				orderId: item.id,
				settlementId: settlementId,
			}));

		if (orderInsert?.length === 0) {
			toast.error(
				"Ju lutem selektoni porosite qe deshironi te i dergoni klientit"
			);
			return false;
		}
		try {
			const response = await api.post(
				"SettlementRefusal/settlement-refusal-processing",
				{
					settlementStatusUpdate: { settlementId: settlementId },
					orderInsert: orderInsert,
				}
			);
			if (response.status === 200) {
				toast.success("Success!");
				setOpenDetailsModal(false);
				setSettlementId(0);
				await fetchTableData();
			}
		} catch (error) {
			console.log(error);
		} finally {
			isSettlementRefusalsDisabled(false);
		}
	};

	const [setConfirmRefusedOrdersDisabled, isSetConfirmRefusedOrdersDisable] =
		useState(false);

	const confirmRefusedOrders = async () => {
		isSetConfirmRefusedOrdersDisable(true);
		try {
			const response = await api.post(
				"SettlementRefusal/client-settlement-refusal-confirmation",
				{ settlementId: settlementId }
			);
			if (response.status === 200) {
				await fetchTableData();
				toast.success("Success!");
				setOpen(false);
			}
		} catch (error) {
			console.log(error);
		} finally {
			isSetConfirmRefusedOrdersDisable(false);
		}
	};

	const [setSettlmentStatusDisabled, isSetSettlmentStatusDisabled] =
		useState(false);

	const settlementStatusUpdate = async (data: any) => {
		isSetSettlmentStatusDisabled(true);
		try {
			const response = await api.post(
				"SettlementRefusal/settlement-status-update",
				data
			);
			if (response.status === 200) {
				await fetchTableData();
				setOpenModal(false);
				setOpenSettlementOrderModal(false);
				toast.success("Sukses!");
			}
		} catch (error) {
			console.log(error);
		} finally {
			isSetSettlmentStatusDisabled(false);
		}
	};

	const confirmRefusalOrdersAreRemovedIntoReturnStorage = async () => {
		const data = {
			settlementId: settlementId,
			settlementStatusId: settlementStatuses.RETURN_STORAGE,
		};
		settlementStatusUpdate(data);
	};

	const getSettlements = async (
		pageSize: number,
		currentPage: number,
		statusIds?: any[],
		code?: any,
		orderDescription?: any,
		fromDate?: any,
		toDate?: any,
		sellerIds?: any[],
		staffIds?: any[],
		paginationDisabled?: boolean
	) => {
		try {
			const responseData = await fetchSettlements(
				pageSize,
				currentPage,
				statusIds,
				code,
				orderDescription,
				fromDate,
				toDate,
				sellerIds,
				staffIds,
				paginationDisabled
			);
			setTableData(responseData?.data);
			setTotal(responseData?.total);
			return responseData;
		} catch (err) {
			console.log(err);
		}
	};

	const onFilterSettlements = (
		code: any[],
		staffIds: any[],
		statusIds: any[],
		orderDescription: any,
		fromDate: any,
		toDate: any,
		sellerIds: any[]
	) => {
		setFilteredSettlements({
			statusIds,
			code,
			orderDescription,
			fromDate,
			toDate,
			sellerIds,
			staffIds,
		});
		setCurrentPage(1);
		getSettlements(
			pageSize,
			1,
			statusIds,
			code,
			orderDescription,
			fromDate,
			toDate,
			sellerIds,
			staffIds
		);
	};

	const customOptions = [
		{
			label: strings?.printlist,
			value: "print_list",
		},
		{
			label: strings?.exportexcel,
			value: "export_excel",
		},
		{
			label: "Dorezo ne depo te kthimit",
			value: "deliver_to_return_storage",
		},
	];
	const refusalOptions = [
		{
			label: strings?.refusalOptions,
			value: "refusedSettlement",
			show: canProcessRefusals,
		},
	];

	/**
	 * The sheet mirrors the grid, currency split included — see the same list on the settlements
	 * page. `sumValue` went out as a bare number and `sumValueEur` did not go out at all, so a
	 * refusal batch with a EUR side exported as its LEK half with nothing to say the rest existed.
	 * Each amount is now followed by a `Valuta` column naming its currency in words; the amount
	 * headers come from the grid, which already names them `Shuma LEK` / `Shuma EUR`.
	 */
	const selectedColumnIds = [
		"id",
		"applicantUserName",
		"applicantFullName",
		"settlementStatusDescription",
		"createdAt",
		"numberOfOrders",
		...moneyExportColumns({
			colId: "sumValue",
			currencyHeaderName: strings?.currencyColumn,
			currency: defaultCurrencyId,
			dualCurrencyEnabled,
			// Off PostmanAL the sheet keeps the single formatted cell it has always had.
			single: (row: any) => formatDualMoneyAmount(row?.sumValue),
		}),
		...(dualCurrencyEnabled
			? moneyExportColumns({
					colId: "sumValueEur",
					currencyHeaderName: strings?.currencyColumn,
					currency: CURRENCY_EUR,
					dualCurrencyEnabled,
			  })
			: []),
		"settlementDescription",
		"confirmedDate",
		"processedBy",
		"deliveredBy",
		"transportingDate",
		"gatheringCity",
	];

	const onExportSelectedId = async () => {
		try {
			const selectedRows = (
				gridRef.current as any
			)?.api?.getSelectedRows();
			let sourceData = selectedRows;

			if (!selectedRows?.length) {
				const fullExportPageSize = total > 0 ? total : 50000;
				const responseData = await fetchSettlements(
					fullExportPageSize,
					1,
					filteredSettlements?.statusIds,
					filteredSettlements?.code,
					filteredSettlements?.orderDescription,
					filteredSettlements?.fromDate,
					filteredSettlements?.toDate,
					filteredSettlements?.sellerIds,
					filteredSettlements?.staffIds,
					true
				);
				sourceData = responseData?.data ?? [];
			}

			const dataToExport = sourceData.map((item: any) => ({
				...item,
				createdAt: valueDateFormatter(item.createdAt),
				id: codeFormatter(item.id),
				confirmedDate:
					item.settlementStatusId === settlementStatuses.CONFIRMED
						? valueDateFormatter(item.confirmedDate)
						: " - ",
				applicantUserName: `${item.applicantUserName} - (${item.uniqueSellerIdentifier})`,
			}));
			exportDataToCsv(gridRef, selectedColumnIds, dataToExport);
		} catch (error) {
			console.log(error);
			toast.error("Gabim gjate eksportit te te dhenave.");
		}
	};

	const onFirstDataRendered = useCallback((params: any) => {
		(gridRef.current as any).api?.sizeColumnsToFit();
	}, []);
	const onSettlementOrderDataRendered = useCallback((params: any) => {
		(settlementOrderRef.current as any).api?.sizeColumnsToFit();
	}, []);

	const isSellerIdNotSame = (data: any) => {
		if (data?.length <= 0) {
			return;
		} else {
			const firstSellerId = data[0]?.sellerId;
			return !data.every((item: any) => item.sellerId === firstSellerId);
		}
	};

	const [
		selectedOrdersForRefusalSettlement,
		setSelectedOrdersForRefusalSettlement,
	] = useState<any>([]);

	const createNewSettlementRefusal = async () => {
		const selectedRows = (
			settlementOrderRef.current as any
		).api.getSelectedRows();

		if (selectedRows?.length <= 0) {
			toast.error(
				"Ju lutem selektoni porosite per te cilat deshironi te krijoni nje refuzim"
			);
			return;
		} else {
			const clientId = selectedRows[0]?.sellerId;
			const processingValidation =
				await fetchSettlementDisableRequestValidation(
					documentTypes.SETTLEMENT_REFUSAL,
					clientId
				);

			if (isSellerIdNotSame(selectedRows)) {
				toast.error(
					"Nuk mund te krijoni barazim te refuzimeve me klient te ndryshem!"
				);
				return;
			} else if (processingValidation) {
				toast.error("Nuk mund te procesoni refuzimet per kete klient");
				return;
			} else {
				setSelectedOrdersForRefusalSettlement(selectedRows);
				setIsCreateRefusedSettlementModalOpen(true);
			}
		}
	};

	const [
		isCreateSettlementRefusalDisabled,
		setIsCreateSettlementRefusalDisabled,
	] = useState(false);

	const confirmCreateForSettlementRefusal = async () => {
		setIsCreateSettlementRefusalDisabled(true);
		const orderData = selectedOrdersForRefusalSettlement.map(
			(item: any) => item?.id
		);
		const settlementRefusal = {
			applicantId: selectedOrdersForRefusalSettlement[0]?.sellerId,
			orderId: orderData,
		};
		try {
			const response = await api.post(
				"SettlementRefusal/create-proc-settlement-for-client",
				settlementRefusal
			);
			if (response.status === 200) {
				toast.success("Sukses");
				await getOrderDetails(null);
				await fetchTableData();
				setIsCreateRefusedSettlementModalOpen(false);
			}
		} catch (error: any) {
			console.log("Error: " + error.message);
			toast.error("Error: " + error.message);
		} finally {
			setIsCreateSettlementRefusalDisabled(false);
		}
	};

	const [
		confirmSettlementRefusalDeliverDisabled,
		setConfirmSettlementRefusalDeliverDisabled,
	] = useState(false);

	const confirmSettlementRefusalDeliver = async () => {
		const data = {
			settlementStatusId: settlementStatuses.DELIVERED,
			settlementId: settlementId,
		};
		setConfirmSettlementRefusalDeliverDisabled(true);
		try {
			const response = await api.post(
				"SettlementRefusal/settlement-status-update",
				data
			);
			if (response.status === 200) {
				toast.success("Refuzimi u dorezua tek klienti me sukses!");
				await getOrderDetails(null);
				await fetchTableData();
				setOpenClientDeliverModal(false);
				setOpenDetailsModal(false);
			}
		} catch (error: any) {
			console.log(error);
			toast.error("Error" + error?.message);
		} finally {
			setConfirmSettlementRefusalDeliverDisabled(false);
		}
	};

	const onRequestButtonClick = useCallback(async () => {
		try {
			const response = await api.get(
				"Settlement/settlement-validations",
				{ params: { documentTypeId: 10 } }
			);
			if (response.status === 200) {
				const result = response?.data;
				setDisableClientRequest(result);
				setOpenRequestModal(true);
			}
		} catch (error: any) {
			console.log("Error: ", error?.message);
			setDisableClientRequest(true);
		}
	}, []);

	const settlementDetailsPdf = async () => {
		const selectedSettlementIds = gridRef?.current?.api
			?.getSelectedRows()
			?.map((item) => ({
				id: item.id,
			}));
		const data = {
			settlementIds: selectedSettlementIds,
			isRefusal: true,
		};

		if (!selectedSettlementIds?.length) {
			toast.warning("Selekto porosite per te printuar");
			return true;
		}
		try {
			const response = await api.post(
				"Settlement/print-settlement",
				data,
				{
					responseType: "blob",
				}
			);
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				console.error(
					"Popup blocked. Please allow popups for this site."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const settlementDetailsPrintRaport = async () => {
		const selectedSettlementIds = gridRef?.current?.api
			?.getSelectedRows()
			?.map((item) => ({
				id: item.id,
			}));

		const data = {
			settlementIds: selectedSettlementIds,
			isRefusal: false,
		};

		if (!selectedSettlementIds?.length) {
			toast.warning("Selekto porosite per te printuar");
			return true;
		}
		try {
			const response = await api.post(
				"Settlement/print-settlement-for-envelope",
				data,
				{
					responseType: "blob",
				}
			);
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				console.error(
					"Popup blocked. Please allow popups for this site."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const renderSellerOptions = () => {
		return seller?.map((sellerItem: any, index: number) => ({
			value: `${sellerItem.id}-${index}`,
			label: `${sellerItem.username}  (${
				sellerItem.uniqueSellerIdentifier ?? ""
			})`,
		}));
	};

	const [searchCode, setSearchCode] = useState("");
	const [selectedSeller, setSelectedSeller] = useState(null);

	const handleIdChange = (e: any) => {
		const code = e.target.value;

		setSearchCode(code);
		filterRefusedOrders(code, selectedSeller, selectedStaff);
	};

	const handleSellerChange = (value: any) => {
		setSelectedSeller(value);
		filterRefusedOrders(searchCode, value, selectedStaff);
	};

	const handleStaffChange = (value: any) => {
		setSelectedStaff(value);
		filterRefusedOrders(searchCode, selectedSeller, value);
	};

	const filterRefusedOrders = useCallback(
		(code: any, sellerId: any, staffId: any) => {
			const filteredSellerId = sellerId?.split("-")[0];
			const filteredStaffId = staffId?.split("-")[0];

			const filteredData = refusedOrders?.filter((item: any) => {
				const createdAt = valueDateFormatter(item.createdAt);
				const refusedDate = valueDateFormatter(item.refusedDate);

				if (
					(!code ||
						(item?.displayId &&
							item.displayId
								.toString()
								.toLowerCase()
								.includes(code.toLowerCase())) ||
						(item?.city &&
							item.city
								.toString()
								.toLowerCase()
								.includes(code.toLowerCase())) ||
						(item?.country &&
							item.country
								.toString()
								.toLowerCase()
								.includes(code.toLowerCase())) ||
						(item?.refid &&
							item.refid
								.toString()
								.toLowerCase()
								.includes(code.toLowerCase())) ||
						(item?.statusDescription &&
							item.statusDescription
								.toString()
								.toLowerCase()
								.includes(code.toLowerCase())) ||
						(item?.buyerNameAndSurname &&
							item.buyerNameAndSurname
								.toString()
								.toLowerCase()
								.includes(code.toLowerCase())) ||
						(item?.orderDescription &&
							item.orderDescription
								.toString()
								.toLowerCase()
								.includes(code.toLowerCase())) ||
						(item?.statusChangeComment &&
							item.statusChangeComment
								.toString()
								.toLowerCase()
								.includes(code.toLowerCase())) ||
						(item?.sellerNameAndSurname &&
							item.sellerNameAndSurname
								.toString()
								.toLowerCase()
								.includes(code.toLowerCase())) ||
						(createdAt &&
							createdAt
								.toString()

								.includes(code)) ||
						(refusedDate &&
							refusedDate
								.toString()

								.includes(code))) &&
					(!filteredSellerId || item.sellerId == filteredSellerId) &&
					(!filteredStaffId ||
						item.lastHistoryUserId == filteredStaffId)
				) {
					return true;
				}
				return false;
			});

			setFiteredRefusedOrders(filteredData);
			setCurrentPage2(1);
		},
		[refusedOrders]
	);

	const onSettlementOrderModalClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "displayId":
				await onOrderIdClicked(params?.data);
				break;
		}
	};

	const settlementsLength = tableData.length;

	useEffect(() => {
		const handleResize = () => {
			if (gridRef.current) {
				(gridRef.current as any).api?.sizeColumnsToFit();
			}
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const scanModalColumns: ColDef[] = [
		{
			headerName: "Order ID",
			field: "id",
			width: 120,
		},
		{
			headerName: "Display ID",
			field: "displayId",
			cellStyle: {
				fontSize: "15px",
				fontWeight: "400",
				lineHeight: "18px",
				color: "#4F16C2",
			},
			width: 150,
		},
		{
			headerName: "Bussiness Name",
			field: "businessName",
			cellStyle: {
				fontSize: "15px",
				fontWeight: "400",
				color: "#2C2C2C",
			},
			width: 200,
		},
		{
			headerName: "Buyer Name",
			field: "buyerNameAndSurname",
			cellStyle: {
				fontSize: "15px",
				fontWeight: "400",
				color: "#4F16C2",
				textDecoration: "underline",
			},
			width: 200,
		},
		{
			headerName: "Buyer Phone",
			field: "buyerPhoneNo",
			cellStyle: {
				fontSize: "15px",
				color: "#4F16C2",
			},
			width: 150,
		},
		{
			headerName: "City",
			field: "city",
			cellStyle: {
				fontSize: "15px",
			},
			width: 120,
		},
		{
			headerName: "Order Price",
			field: "orderPrice",
			valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
			width: 120,
		},
		{
			headerName: "Package Description",
			field: "orderDescription",
			cellStyle: {
				fontSize: "15px",
				fontWeight: "400",
			},
			width: 180,
		},
	];

	const playErrorSound = () => {
		const audio = new Audio(require("../../assets/sounds/errorSound.mp3"));
		audio.play();
	};

	const [disableOrderScan, setDisableOrderScan] = useState<boolean>(false);

	const handleScanInput = async (e: any) => {
		if (!scannedOrderId) {
			return;
		}
		if (disableOrderScan) return;
		const removedPrefixInput = scannedOrderId
			.toLocaleUpperCase()
			.replace("OR-", "");
		setDisableOrderScan(true);

		try {
			const response = await api.post(
				"SettlementRefusal/select-details-for-international-RefusedOrders",
				null,
				{ params: { orderId: removedPrefixInput } }
			);
			if (response.status === 200) {
				const orderDetails = response.data;
				const checkifscanned = scannedOrdeDetails?.some(
					(item: any) => item.id === orderDetails[0].id
				);
				if (checkifscanned) {
					playErrorSound();
					toast.warning("Porosia eshte skenuar nje here");
					return;
				}
				if (scannedOrdeDetails.length === 0) {
					setScannedOrdeDetails(orderDetails);
				} else {
					setScannedOrdeDetails((prevDetails: any) => [
						...orderDetails,
						...prevDetails,
					]);
				}
			}
		} catch (error) {
			playErrorSound();
			console.log(error);
		} finally {
			setDisableOrderScan(false);
			setScannedOrderId("");
		}
	};

	const cleanStateAndScanModal = async () => {
		setScannedOrderId("");
		setScannedOrdeDetails([]);
		setOpenScanModal(false);
		await fetchTableData();
		await getOrderDetails(null);
	};
	const [disableProcFromScanning, setDisableProcFromScanning] =
		useState<boolean>(false);
	const handleSettlementProcessingFromScanning = async () => {
		if (disableClientRequest) return;
		const orderIds = scannedOrdeDetails
			.map((item) => item.id)
			.filter((id, index, array) => array.indexOf(id) === index);
		setDisableProcFromScanning(true);
		try {
			const response = await api.post(
				"SettlementRefusal/test",
				orderIds,
				{
					responseType: "blob",
				}
			);
			if (response.status === 200) {
				const file = response.data;
				const fileBlob = new Blob([file], { type: "application/pdf" });
				const fileUrl = window.URL.createObjectURL(fileBlob);

				const pdfWindow = window.open();
				if (pdfWindow) {
					pdfWindow.location.href = fileUrl;
				} else {
					console.error(
						"Popup blocked. Please allow popups for this site."
					);
				}
				cleanStateAndScanModal();
				toast.success("Sukses!");
			}
			console.log(response.data);
		} catch (error) {
			console.log(error);
		} finally {
			setDisableProcFromScanning(false);
			cleanStateAndScanModal();
		}
	};

	const [pendingUpdates, setPendingUpdates] = useState<
		{ settlementId: number; settlementStatusId: number }[]
	>([]);

	const [isStatusChangeBusy, setIsStatusChangeBusy] = useState(false);

	const openStatusChangeConfirmation = () => {
		const selectedRows = (gridRef.current as any).api.getSelectedRows();

		const hasInvalidRow = selectedRows.some(
			(r: any) => r.settlementStatusId !== settlementStatuses.PROCESSING
		);

		if (hasInvalidRow) {
			toast.error(
				"Ju lutem selektoni vetem barazimet me status: 'Duke u Procesuar'"
			);
			return;
		}

		const updates = selectedRows.map((r: any) => ({
			settlementId: r.id,
			settlementStatusId: settlementStatuses.RETURN_STORAGE,
		}));

		setPendingUpdates(updates);
		setOpenStatusChangeModalConfirmation(true);
	};

	const sendStatusUpdates = async () => {
		setIsStatusChangeBusy(true);
		try {
			const response = await api.post(
				"SettlementRefusal/settlement-status-update-list",
				pendingUpdates
			);
			if (response.status === 200) {
				await fetchTableData();
				toast.success("Sukses!");
				setOpenStatusChangeModalConfirmation(false);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsStatusChangeBusy(false);
		}
	};

	return (
		<>
			<div className="settlementContainer">
				<div className="settlementTable">
					<div className="settlementInfo">
						<FilterSettlementsSection
							title={strings?.filterSettlementsTitle}
							buttonTitle={strings?.filterSettlementsButton}
							showRequestButton={isClient}
							onFilterChange={onFilterSettlements}
							selectedOptions={customOptions}
							onSelectedOrderStatusChange={onExportSelectedId}
							showAdminFilters={isAdmin}
							showReturnWarehouseManager={
								isReturnWarehouseManager
							}
							onRequestButtonClick={onRequestButtonClick}
							onPrintTickets={settlementDetailsPrintRaport}
							settlementLength={settlementsLength}
							isRefusal={true}
							deliverToReturnStorage={
								openStatusChangeConfirmation
							}
							staffList={staffList}
							sellerList={seller}
						/>
					</div>
					<div className="flex flex-col justify-center bg-white p-2">
						<PaginationTable
							current={currentPage}
							pageSize={pageSize}
							total={total}
							startOrderIndex={startOrderIndex}
							endOrderIndex={endOrderIndex}
							onChange={onPageChanged}
							onShowSizeChange={handlePageSizeChange}
						/>
						<div
							className="ag-theme-alpine"
							style={{
								height: 350,
								width: "100%",
								overflowX: "auto",
							}}
						>
							<AgGridReact
								rowData={tableData}
								columnDefs={tableColumns}
								defaultColDef={defaultColDef}
								undoRedoCellEditing={true}
								undoRedoCellEditingLimit={20}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								defaultCsvExportParams={
									defaultExcelExportSettings
								}
								rowSelection="multiple"
								ref={gridRef}
								enableRangeSelection={true}
								cacheQuickFilter={true}
								rowHeight={60}
								onCellClicked={onCellClicked}
								onFirstDataRendered={onFirstDataRendered}
							/>
						</div>
					</div>
				</div>
				{(isAdmin || isReturnWarehouseManager) && (
					<div className="settlementTable">
						<div className={classes.container}>
							<h1 className={classes.filterOrderTitle}>
								{strings?.refusalOrdersTitle}
							</h1>
							<div className={classes.inputs}>
								<DefaultInput
									placeholder={strings?.searchCodePlaceholder}
									onChange={handleIdChange}
									allowClear={true}
									label={strings?.searchCodeLabel}
									prefix={<CodeIcon />}
									className={classes.input}
									value={searchCode}
								/>
								<DefaultSelect
									label={strings?.sellerLabel}
									flexDirection="col"
									alignCenter="start"
									className={classes.input}
									placeholder={strings?.sellerPlaceholder}
									value={selectedSeller}
									onChange={handleSellerChange}
									allowClear={true}
									options={renderSellerOptions()}
								/>
								<DefaultSelect
									placeholder={strings?.optionsPlaceholder}
									label={strings?.optionsLabel}
									options={refusalOptions?.filter(
										(option: any) => option.show !== false
									)}
									onChange={(value: any) => {
										if (value === "refusedSettlement") {
											createNewSettlementRefusal();
										}
									}}
									flexDirection="col"
									alignCenter="start"
									className={classes.input}
									allowClear={true}
								/>
								<DefaultSelect
									placeholder={strings?.responsible}
									label={strings?.responsible}
									options={staffOptions}
									onChange={handleStaffChange}
									value={selectedStaff ?? undefined}
									flexDirection="col"
									alignCenter="start"
									className={classes.input}
									allowClear={true}
								/>
								<div className="flex justify-center items-end">
									<Button
										type="default"
										size="large"
										style={{
											color: "#4f16c2",
											width: "100%",
										}}
										onClick={() => setOpenScanModal(true)}
									>
										Skano
									</Button>
								</div>
							</div>
						</div>
						<div className="flex flex-col justify-center bg-white p-2">
							<PaginationTable
								current={currentPage2}
								pageSize={pageSize2}
								total={refusedOrders?.length}
								startOrderIndex={startOrderIndex2}
								endOrderIndex={endOrderIndex2}
								onChange={onPageChanged2}
								onShowSizeChange={handlePageSizeChange2}
							/>
							<div
								className="ag-theme-alpine"
								style={{
									height: 450,
									width: "100%",
								}}
							>
								<AgGridReact
									rowData={paginatedOrderRefusals}
									columnDefs={orderTableColumns}
									defaultColDef={defaultColDef}
									undoRedoCellEditing={true}
									undoRedoCellEditingLimit={20}
									defaultExcelExportParams={
										defaultExcelExportSettings
									}
									defaultCsvExportParams={
										defaultExcelExportSettings
									}
									rowSelection="multiple"
									ref={settlementOrderRef}
									enableRangeSelection={true}
									cacheQuickFilter={true}
									rowHeight={60}
									onFirstDataRendered={
										onSettlementOrderDataRendered
									}
									onCellClicked={onRefusedOrdersCellClicked}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
			<Modal
				open={open}
				title={`${strings?.confirmModalTitle}${settlementId}`}
				onCancel={() => setOpen(false)}
				onOk={confirmRefusedOrders}
				okButtonProps={{
					className: "bg-blue-500",
					disabled: setConfirmRefusedOrdersDisabled,
				}}
			>
				<Divider />
				<p>{`${strings?.confirmModalText}${settlementId}`}</p>
			</Modal>
			<Modal
				open={openDetailsModal}
				// `settlementData` belongs to the row-click flow; `openRefusalModal` opens this
				// one and sets `settlementId`, so the title used to read "Nr.undefined".
				title={`${strings?.modalDetailsTitle}${settlementId}`}
				onOk={() => setOpenDetailsModal(false)}
				onCancel={() => setOpenDetailsModal(false)}
				width={2000}
				footer={() => (
					<>
						<div className="flex justify-end ">
							<Button
								size="large"
								icon={<CloseOutlined />}
								onClick={() => setOpenDetailsModal(false)}
							>
								{strings?.cancelButtonText}
							</Button>
							<Button
								type="primary"
								size="large"
								// className="bg-blue-600"
								style={{ backgroundColor: "#4F16C2" }}
								icon={<CheckOutlined />}
								onClick={settlementRefusalProcessing}
								disabled={settlementRefusalDisabled}
							>
								{strings?.submitButtonText}
							</Button>
						</div>
					</>
				)}
			>
				<Divider />
				<div className="flex flex-col justify-center bg-white p-2">
					<div
						className="ag-theme-alpine"
						style={{
							height: 600,
							width: "100%",
						}}
					>
						<AgGridReact
							rowData={refusedOrders}
							columnDefs={orderTableColumns}
							defaultColDef={defaultColDef}
							suppressCopyRowsToClipboard={true}
							suppressRowClickSelection={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							defaultCsvExportParams={defaultExcelExportSettings}
							rowSelection="multiple"
							ref={refusalGridRef}
							cacheQuickFilter={true}
						/>
					</div>
				</div>
			</Modal>
			<Modal
				open={openModal}
				onCancel={() => setOpenModal(false)}
				onOk={confirmRefusalOrdersAreRemovedIntoReturnStorage}
				title={<ExclamationCircleFilled size={160} />}
				okButtonProps={{
					className: "bg-blue-500",
					disabled: setSettlmentStatusDisabled,
				}}
			>
				<p>{strings?.confirmDeliverToReturnStorageText}</p>
			</Modal>
			<Modal
				open={openSettlementOrderModal}
				// Same flow, same fix as the details modal above.
				title={`${strings?.modalDetailsTitle}${settlementId}`}
				width={1000}
				onCancel={() => setOpenSettlementOrderModal(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				footer={() => (
					<>
						{(isAdmin || isReturnWarehouseManager) && (
							<div className="flex flex-row justify-end ">
								<Button
									type="default"
									size="large"
									onClick={() =>
										setOpenSettlementOrderModal(false)
									}
								>
									{strings?.cancelButtonText}
								</Button>
								{settlementRefusalData.settlementStatusId ===
									settlementStatuses.PROCESSING && (
									<Button
										type="primary"
										size="large"
										// className="bg-blue-600"
										style={{ backgroundColor: "#4F16C2" }}
										onClick={() => setOpenModal(true)}
									>
										{strings?.deliverToReturnStorageText}
									</Button>
								)}
								{settlementRefusalData.settlementStatusId ===
									settlementStatuses.RETURN_STORAGE && (
									<Button
										type="primary"
										size="large"
										// className="bg-blue-600"
										style={{ backgroundColor: "#4F16C2" }}
										onClick={() =>
											setOpenClientDeliverModal(true)
										}
									>
										{strings?.deliverToClientText}
									</Button>
								)}
							</div>
						)}
					</>
				)}
			>
				<>
					<div>
						<Divider />
						<div
							className="ag-theme-alpine"
							style={{
								height: 600,
								width: "100%",
							}}
						>
							<AgGridReact
								rowData={settlementOrderData}
								columnDefs={settlementOrderTableColumns}
								defaultColDef={defaultColDef}
								suppressCopyRowsToClipboard={true}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								defaultCsvExportParams={
									defaultExcelExportSettings
								}
								// domLayout="autoHeight"
								ref={settlementOrderGridRef}
								enableRangeSelection={true}
								cacheQuickFilter={true}
								rowHeight={60}
								onCellClicked={onSettlementOrderModalClicked}
							/>
						</div>
					</div>
				</>
			</Modal>
			<Modal
				open={isCreateRefusedSettlementModalOpen}
				onCancel={() => setIsCreateRefusedSettlementModalOpen(false)}
				onOk={confirmCreateForSettlementRefusal}
				title={strings?.confirmCreateSettlementRefusalText}
				okButtonProps={{
					className: "bg-blue-500",
					color: "white",
					disabled: isCreateSettlementRefusalDisabled,
				}}
			>
				<p>{strings?.areYouSureToProcessClientRefusalsText}</p>
			</Modal>
			<Modal
				open={openClientDeliverModal}
				onCancel={() => setOpenClientDeliverModal(false)}
				onOk={confirmSettlementRefusalDeliver}
				title={strings?.confirmClientDeliveryText}
				okButtonProps={{
					className: "bg-blue-500",
					disabled: confirmSettlementRefusalDeliverDisabled,
				}}
			>
				<p>{strings?.confirmClientDeliveryText}</p>
			</Modal>
			<Modal
				open={openStatusChangeModalConfirmation}
				onCancel={() => setOpenStatusChangeModalConfirmation(false)}
				onOk={sendStatusUpdates}
				title={strings?.confirmDeliverToReturnStorageText}
				okButtonProps={{
					className: "bg-blue-500",
					disabled: isStatusChangeBusy,
				}}
			></Modal>
			<Modal
				open={openModalSmallDetailsAndHistory}
				title={`${strings?.modalSmallDetailsTitle}${settlementData?.id}`}
				onCancel={() => setOpenModalSmallDetailsAndHistory(false)}
				footer={() => (
					<>
						<div className="flex items-end justify-end">
							<Button
								size="large"
								icon={<CloseOutlined />}
								onClick={() =>
									setOpenModalSmallDetailsAndHistory(false)
								}
							>
								{strings?.closeButtonText}
							</Button>
						</div>
					</>
				)}
			>
				<Divider />
				<div>
					<OrderDetailRow
						label="Kodi"
						style={style.oddRow}
						value={settlementData?.id}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label="Data"
						value={settlementData?.createdAt}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label="Statusi"
						style={style.oddRow}
						value={settlementData?.settlementStatusDescription}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label="Klienti"
						value={settlementData?.applicantFullName}
						className="driverDetailRow"
					/>
					{/* Both halves stated separately: a settlement spanning both currencies is
					    paid out as two cash-register transactions, so there is no single total.
					    currencySymbol() with no id falls back to the deployment symbol. */}
					<OrderDetailRow
						label="Vlera"
						style={style.oddRow}
						value={`${settlementData?.sumValue.toFixed(
							2
						)} (${currencySymbol()})${
							settlementData?.sumValueEur
								? ` + ${settlementData.sumValueEur.toFixed(
										2
								  )} (${currencySymbol(CURRENCY_EUR)})`
								: ""
						}`}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label="Porosi"
						value={settlementData?.numberOfOrders}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label="Komentet"
						style={style.oddRow}
						value={settlementData?.settlementDescription}
						className="driverDetailRow"
					/>
				</div>
				<Divider />
				<div
					onClick={() =>
						setOpenSettlementHistory(!openSettlementHistory)
					}
					className="flex flex-row gap-2"
				>
					{openSettlementHistory ? <p>{"▼"}</p> : <p>{"▶"}</p>}
					<p>{strings?.historyToggleText}</p>
				</div>
				{openSettlementHistory && (
					<Steps
						progressDot
						current={10}
						direction="vertical"
						items={settlementHistory}
					/>
				)}
			</Modal>
			<Modal
				open={openRequestModal}
				title={strings?.requestModalTitle}
				onOk={() => setOpenRequestModal(false)}
				onCancel={() => setOpenRequestModal(false)}
				okButtonProps={{ className: "bg-blue-500" }}
				footer={(_, { OkBtn, CancelBtn }) => (
					<>
						<div className="flex items-center justify-center">
							<Button
								type="primary"
								size="large"
								// className="bg-blue-600"
								style={{
									backgroundColor: "#4F16C2",
									color: "white",
								}}
								onClick={onSettlementRefusalRequestSend}
								disabled={
									disableClientRequest ||
									setSettlementRefusalRequestDisabled
								}
							>
								{strings?.requestModalButton}
							</Button>
						</div>
					</>
				)}
			>
				<Divider />
				<div className="flex flex-col gap-4">
					{disableClientRequest ? (
						<Alert
							type="warning"
							showIcon
							message={strings?.disableClientRequestWarning}
							description={settlementModalDescription}
						/>
					) : (
						<></>
					)}
					<TextArea
						rows={4}
						value={settlementDescription}
						onChange={(e: any) =>
							setSettlementDescription(e.target.value)
						}
					/>
				</div>
				<Divider />
			</Modal>

			<Modal
				open={openScanModal}
				title={"Skano porosite e refuzuara dhe proceso"}
				onOk={() => setOpenScanModal(false)}
				onCancel={cleanStateAndScanModal}
				width={1000}
				footer={() => (
					<>
						<div className="flex justify-end ">
							<Button
								size="large"
								icon={<CloseOutlined />}
								onClick={cleanStateAndScanModal}
							>
								{strings?.cancelButtonText}
							</Button>
							<Button
								type="primary"
								size="large"
								// className="bg-blue-600"
								style={{ backgroundColor: "#4F16C2" }}
								icon={<CheckOutlined />}
								onClick={handleSettlementProcessingFromScanning}
								disabled={disableProcFromScanning}
							>
								{strings?.submitButtonText}
							</Button>
						</div>
					</>
				)}
			>
				<Divider />
				<div className="flex flex-col justify-center bg-white p-2">
					<Input
						value={scannedOrderId}
						placeholder="Skano Porosi"
						onChange={(e) => setScannedOrderId(e.target.value)}
						onPressEnter={handleScanInput}
						className={classes.input}
					/>
					<div
						className="ag-theme-alpine"
						style={{
							height: 600,
							width: "100%",
						}}
					>
						<AgGridReact
							rowData={scannedOrdeDetails}
							columnDefs={scanModalColumns}
							defaultColDef={defaultColDef}
							suppressCopyRowsToClipboard={true}
							suppressRowClickSelection={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							defaultCsvExportParams={defaultExcelExportSettings}
							// rowSelection="multiple"
							ref={scanedOrderGridRef}
							cacheQuickFilter={true}
						/>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default SettlementRefusal;

const style = {
	oddRow: { backgroundColor: "#F9F8FC" },
};
